import { booleanAttribute, Component, computed, inject, input } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { IconComponent } from '@reforgium/icon';
import { LangPipe } from '@reforgium/presentia';
import { SkButtonComponent } from '@ss/ui';
import { SkSkeletonComponent } from '@ss/ui';

import { urls } from 'constants/urls.constant';

import { AuthService } from 'use-cases/auth.service';

@Component({
  selector: 'app-article-card',
  template: `
    <article class="card flex justify-between flex-row w-full">
      <div class="card-text">
        @if (isSkeleton()) {
          <div class="grid gap-2 mb-2">
            <sk-skeleton width="70%" height="22px" />
            <sk-skeleton width="90%" height="19px" />
            <sk-skeleton width="90%" height="19px" />
          </div>
        } @else {
          <div class="card-description flex flex-col md:flex-row items-start gap-x-6">
            <span class="badge {{ statusColor() }}">{{ statusText() | lang }}</span>
            <span class="revision-badge">{{ 'common.version-count' | lang }}: {{ versionCount() }} </span>
          </div>
          <h2 class="title-s">{{ title() }}</h2>
        }
        <a
          class="link-text"
          [attr.aria-label]="title()"
          [routerLink]="isSkeleton() ? null : [documentId()]"
          [queryParams]="{
            versionId: this.versionId(),
            categoryId: this.categoryId(),
          }"
        >
          {{ 'common.navigate' | lang }}
        </a>
      </div>

      <div class="flex flex-col justify-between items-end">
        @if (this.authService.isAuthenticated) {
          <sk-button
            appearance="contrast"
            icon="edit"
            [label]="'administrative.buttons.edit' | lang"
            (clicked)="handleDocumentEdit()"
          />
        }
        <a
          class="card-arrow"
          [attr.aria-label]="title()"
          [routerLink]="isSkeleton() ? null : [documentId()]"
          [queryParams]="{
            versionId: this.versionId(),
            categoryId: this.categoryId(),
          }"
        >
          <re-icon name="chevron" width="12" height="12" />
        </a>
      </div>
    </article>
  `,
  imports: [RouterLink, LangPipe, SkButtonComponent, IconComponent, SkSkeletonComponent],
  styles: `
    .card {
      position: relative;
      flex-direction: row;
      padding: 16px;
      gap: 16px;
      border-radius: 16px !important;
      box-shadow: 0 3px 8px #18284405;
    }
    .card-text {
      flex: 1 1 auto;
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .card > :last-child {
      flex-shrink: 0;
    }
    .title-s {
      margin: 10px 0 0;
      font-size: calc(13px * var(--a11y-font-scale, 1));
      font-weight: 400;
      line-height: 1.4;
      color: var(--text-primary);
    }
    .card-description {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }
    .badge,
    .revision-badge {
      display: inline-flex;
      border: 1px solid currentColor;
      border-radius: 20px;
      padding: 0 5px;
      height: auto;
      font-size: calc(10px * var(--a11y-font-scale, 1));
      line-height: 1.4;
      font-weight: 400;
    }
    .revision-badge {
      color: #168de2;
      background: #eaf6ff;
    }
    .active {
      color: #21a366;
      background: #f1fff7;
    }
    .danger {
      color: var(--danger-dark);
      background: var(--danger-light);
    }
    .link-text {
      position: absolute;
      inset: 0;
      overflow: hidden;
      font-size: 0;
      border-radius: 16px;
    }
    .link-text:focus-visible {
      outline: 2px solid #2da5fa;
    }
    .card-arrow {
      margin-block: auto;
      color: var(--text-secondary);
    }
    sk-button,
    .card-arrow {
      position: relative;
      z-index: 1;
    }
  `,
})
export class ArticleCardComponent {
  versionId = input<number>();
  categoryId = input<number | string>();
  documentId = input<number>();
  title = input<string>();
  status = input<'ACTIVE' | 'REPEALED'>();
  description = input<string>();
  versionCount = input<number>();
  isSkeleton = input(false, {
    transform: booleanAttribute,
  });

  private router = inject(Router);
  protected authService = inject(AuthService);

  statusText = computed(() => (this.status() === 'ACTIVE' ? 'common.status.active' : 'common.status.repealed'));
  statusColor = computed(() => (this.status() === 'ACTIVE' ? 'active' : 'danger'));

  protected readonly urls = urls;

  handleDocumentEdit() {
    void this.router.navigate([urls.documentEdit(this.documentId())], {
      queryParams: { versionId: this.versionId(), categoryId: this.categoryId() },
    });
  }
}
